// apps/web/src/hooks/useDashboardPrefsCounts.ts
//
// Dashboard-only wrapper around `useUserPreferences` that derives the
// count-shaped view the prefs filter chip consumes. Centralised here so
// `clearTemporary` (the chip's "Clear" button) has a single canonical
// implementation we can swap once the design playbook lands a temporary-
// override pattern.
//
// Captain Input #4 (locked, 2026-05-02 spec): existing users with no
// preferences continue to get the global view. We do NOT synthesise
// defaults on their behalf. When the user is logged out OR has no
// `user_preferences` row, every count is 0 and the chip renders nothing.

import { useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useUserPreferences } from '@/hooks/queries'

export interface DashboardPrefsCounts {
  sectorCount: number
  countryCount: number
  vendorCount: number
  /**
   * "Clear" action surfaced by the prefs chip. v1: a no-op stub — the
   * chip-level clear belongs to a Wave-3 follow-up that wires temporary-
   * vs-saved overrides through the page-level filter state.
   * Tracked at: TODO(P7-followup) wire chip-clear to temporary overrides.
   */
  clearTemporary: () => void
}

export function useDashboardPrefsCounts(): DashboardPrefsCounts {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const prefsQuery = useUserPreferences(userId)

  const clearTemporary = useCallback(() => {
    // No-op v1 — see follow-up note above.
  }, [])

  const prefs = prefsQuery.data
  const sectorCount = prefs?.defaultIndustries?.length ?? 0
  const countryCount = prefs?.defaultRegions?.length ?? 0
  const vendorCount = prefs?.trackedVendors?.length ?? 0

  return { sectorCount, countryCount, vendorCount, clearTemporary }
}
